import { useId } from 'react';

type TileLabel = {
  index: number;
  label: string;
};

type CircularProgressSvgProps = {
  totalTiles: number;
  outerRadius: number;
  innerRadius: number;
  gapDegrees: number;
  labels: TileLabel[];
  getTileColor: (index: number) => string;
  tileStateProvider?: (index: number) => { ratio: number; color: string };
  className?: string;
};

export const CircularProgressSvg = ({
  totalTiles,
  outerRadius,
  innerRadius,
  gapDegrees,
  labels,
  getTileColor,
  tileStateProvider,
  className,
}: CircularProgressSvgProps) => {
  const uid = useId();
  const glowFilterId = `centerGlow-${uid}`;
  const viewBoxSize = 2 * (outerRadius + 12);
  const center = viewBoxSize / 2;

  const degToRad = (deg: number) => (deg * Math.PI) / 180;

  const createTilePath = (startDeg: number, endDeg: number) => {
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    const startOuter = {
      x: center + outerRadius * Math.cos(degToRad(startDeg)),
      y: center + outerRadius * Math.sin(degToRad(startDeg)),
    };
    const endOuter = {
      x: center + outerRadius * Math.cos(degToRad(endDeg)),
      y: center + outerRadius * Math.sin(degToRad(endDeg)),
    };
    const startInner = {
      x: center + innerRadius * Math.cos(degToRad(endDeg)),
      y: center + innerRadius * Math.sin(degToRad(endDeg)),
    };
    const endInner = {
      x: center + innerRadius * Math.cos(degToRad(startDeg)),
      y: center + innerRadius * Math.sin(degToRad(startDeg)),
    };
    return `
      M ${startOuter.x} ${startOuter.y}
      A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
      L ${startInner.x} ${startInner.y}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}
      Z
    `;
  };

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`} className={className}>
      {(() => {
        const diameterRatio = 0.4;
        const glowRadius = (viewBoxSize * diameterRatio) / 2;
        const blurStd = (60 / 340) * viewBoxSize;
        return (
          <>
            <g filter={`url(#${glowFilterId})`}>
              <circle cx={center} cy={center} r={glowRadius} fill="#2D5EFF" />
            </g>
            <defs>
              <filter
                id={glowFilterId}
                x="0"
                y="0"
                width={viewBoxSize}
                height={viewBoxSize}
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation={blurStd} />
              </filter>
            </defs>
          </>
        );
      })()}

      {Array.from({ length: totalTiles }, (_, index) => {
        const sweep = 360 / totalTiles;
        const base = -90 - sweep / 2;
        const start = base + index * sweep + gapDegrees / 2;
        const end = base + (index + 1) * sweep - gapDegrees / 2;
        const mid = (start + end) / 2;
        const label = labels.find((t) => t.index === index)?.label;
        const labelR = (outerRadius + innerRadius) / 2;
        const lx = center + labelR * Math.cos(degToRad(mid));
        const ly = center + labelR * Math.sin(degToRad(mid));
        const baseFill = '#314272';
        const fill = getTileColor(index);

        return (
          <g key={index}>
            {/* Base tile */}
            <path d={createTilePath(start, end)} fill={tileStateProvider ? baseFill : fill} stroke="none" />
            {/* Active overlay if provided */}
            {tileStateProvider &&
              (() => {
                const { ratio, color } = tileStateProvider(index);
                const clamped = Math.max(0, Math.min(1, ratio));
                if (clamped <= 0) return null;
                const activeEnd = start + (end - start) * clamped;
                return <path d={createTilePath(start, activeEnd)} fill={color} stroke="none" />;
              })()}
            {label && (
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${mid + 90}, ${lx}, ${ly})`}
                fill="#ffffff"
                style={{ fontFamily: 'Jost', fontWeight: 700, fontSize: 12 }}
              >
                {label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};
